import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { companyPayouts } from '../../api/company/client'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { Loader2, FileText } from 'lucide-react'

const STATUS_KEYS: Record<string, string> = {
  completed: 'company.statusCompleted',
  pending: 'company.statusPending',
  failed: 'company.statusFailed',
  scheduled: 'company.statusScheduled',
  processing: 'company.statusProcessing',
}

export default function CompanyPayouts() {
  const { t } = useTranslation('portal')
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['company', 'payouts'],
    queryFn: async () => (await companyPayouts.list({ limit: 50 })).data,
  })
  const { data: currentData } = useQuery({
    queryKey: ['company', 'payouts', 'current'],
    queryFn: async () => (await companyPayouts.current()).data,
  })

  const items = (listData?.items ?? []) as Array<{
    id: string
    period_start: string
    period_end: string
    total_amount: number
    total_bank_fee: number
    total_platform_fee: number
    net_amount: number
    transaction_count: number
    currency: string
    status: string
    reference: string | null
    processed_at: string | null
    created_at: string
  }>
  const current = currentData

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('company.payouts.title')}</h1>

      {current && (
        <div className="rounded-xl bg-white border border-gray-200 p-6 mb-6 shadow-sm">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" /> {t('company.payouts.currentPeriod')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-gray-500 text-sm">{t('company.payouts.period')}</p>
              <p className="text-gray-900">
                {format(new Date(current.period_start), 'MMM d')} –{' '}
                {format(new Date(current.period_end), 'MMM d, yyyy')}
              </p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{t('company.payouts.transactions')}</p>
              <p className="text-gray-900">{current.transaction_count}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{t('company.payouts.revenueBam')}</p>
              <p className="text-gray-900">{Number(current.total_revenue).toFixed(2)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">{t('company.payouts.estPayoutBam')}</p>
              <p className="text-blue-600 font-semibold">
                {Number(current.estimated_net_amount).toFixed(2)}
              </p>
            </div>
          </div>
          {current.next_payout_date != null && current.days_until_payout != null && (
            <p className="text-gray-500 text-sm mt-3">
              {t('company.payouts.nextPayoutDays', {
                date: format(new Date(current.next_payout_date), 'PPP'),
                days: current.days_until_payout,
              })}
            </p>
          )}
        </div>
      )}

      <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 p-4 border-b border-gray-200">
          {t('company.payouts.payoutHistory')}
        </h2>
        {listLoading ? (
          <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" /> {t('company.payouts.loading')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left min-w-[560px]">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500 text-sm">
                  <th className="p-3">{t('company.payouts.period')}</th>
                  <th className="p-3">{t('company.payouts.transactions')}</th>
                  <th className="p-3">{t('company.payouts.netAmount')}</th>
                  <th className="p-3">{t('company.overview.status')}</th>
                  <th className="p-3">{t('company.payouts.processed')}</th>
                  <th className="p-3">{t('company.payouts.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-gray-500 text-center">
                      {t('company.payouts.noPayoutsYet')}
                    </td>
                  </tr>
                ) : (
                  items.map((p) => (
                    <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="p-3">
                        <Link
                          to={`/company/payouts/${p.id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {format(new Date(p.period_start), 'MMM d')} –{' '}
                          {format(new Date(p.period_end), 'MMM d, yyyy')}
                        </Link>
                      </td>
                      <td className="p-3 text-gray-700">{p.transaction_count}</td>
                      <td className="p-3 text-gray-900">
                        {Number(p.net_amount).toFixed(2)} {p.currency}
                      </td>
                      <td className="p-3">
                        <span
                          className={`rounded px-2 py-0.5 text-xs ${
                            p.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : p.status === 'failed'
                                ? 'bg-red-100 text-red-700'
                                : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {STATUS_KEYS[p.status] ? t(STATUS_KEYS[p.status]) : p.status}
                        </span>
                      </td>
                      <td className="p-3 text-gray-500 text-sm">
                        {p.processed_at
                          ? format(new Date(p.processed_at), 'PP')
                          : '—'}
                      </td>
                      <td className="p-3 flex items-center gap-2">
                        <Link
                          to={`/company/payouts/${p.id}`}
                          className="text-sm text-blue-600 hover:underline"
                        >
                          {t('company.payouts.details')}
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
